import { joinIds } from '@/clientOnlyUtils/clientUtils';

import type { ContactCardBodyCadenceStep } from '@poseidon/components/ContactCardBodyPure';
import type CadenceState from '@poseidon/types/pg-gen/cadenceState';
import type CadenceStepState from '@poseidon/types/pg-gen/cadenceStepState';
import type CadenceStepType from '@poseidon/types/pg-gen/cadenceStepType';
import type CadenceStepOption from '@poseidon/types/pg-gen/cadenceStepOption';
import { type ContactCardOrgRowOrg } from '@poseidon/components/ContactCardOrgRowPure';

import { type SearchParams } from '@/hooks/useRouterQuery';
import { type ContactCardUser } from '~/components/ContactCard/ContactCardPure';
import { type ContactCardBodyCadence, type ContactCardBodyContact } from '~/components/ContactCard/ContactCardTypes';

// Re-export local types for callsite convenience
export type { ContactCardBodyCadence, ContactCardBodyContact } from '~/components/ContactCard/ContactCardTypes';
export type { ContactCardUser, ContactCardCadenceActionsReminder } from '~/components/ContactCard/ContactCardPure';

type CadenceStepConverterInput = {
  id: string;
  parent_id: string | null;
  type: CadenceStepType;
  state: CadenceStepState;
  option: CadenceStepOption;
  archived: boolean | null;
  computedError?: {
    default?: string;
    body2?: string;
    subject?: string;
  };
};

type OrgConverterInput = {
  id: string;
  primaryFields?: { name?: { data: string } | null } | null;
  primaryPictureUrl?: string | null;
};

type CadenceConverterInput = {
  id: string;
  isActive: boolean;
  name: string;
  state: CadenceState;
  running: boolean;
  nextStepTypes?: CadenceStepType[] | null;
  interruptMessage: string | null;
};

type ContactConverterInput = {
  id: string;
  primaryPictureUrl?: string | null;
  liOpenLink?: boolean | null;
  displayHonorific?: boolean;
  stageId?: string | null;
  signalsComputed?: boolean;
  triggeredSignals?: { bucketKey: string; bucketName: string; reason: string | null }[] | null;
  primaryFields: {
    firstName?: { data?: string | null } | null;
    lastName?: { data?: string | null } | null;
    phone?: { data?: string | null; verified?: boolean | null } | null;
    professionalEmail?: { data?: string | null; verified?: boolean | null } | null;
    title?: { data?: string | null } | null;
    honorificTitle?: { data?: string | null } | null;
  };
};

export function cadenceOldToNewFormat(
  cadence: CadenceConverterInput | null | undefined,
  contactUser?: ContactCardUser | null,
): ContactCardBodyCadence | undefined {
  if (cadence == null) return undefined;

  return {
    id: cadence.id,
    isActive: cadence.isActive,
    name: cadence.name,
    state: cadence.state,
    running: cadence.running,
    nextStepTypes: cadence.nextStepTypes ?? [],
    interruptMessage: cadence.interruptMessage,
    user_li_connected: contactUser?.liConnected ?? null,
    user_li_connected_at: contactUser?.liConnectedAt ?? null,
    user_profile_picture: contactUser?.profilePicture ?? null,
    user_email: contactUser?.email ?? null,
    user_name: contactUser?.name ?? null,
  };
}

export function contactOldToNewFormat(contact: ContactConverterInput): ContactCardBodyContact {
  return {
    id: contact.id,
    firstName: contact.primaryFields?.firstName?.data,
    lastName: contact.primaryFields?.lastName?.data,
    photoUrl: contact.primaryPictureUrl,
    phone: contact.primaryFields?.phone?.data,
    phoneVerified: contact.primaryFields?.phone?.verified,
    professionalEmail: contact.primaryFields?.professionalEmail?.data,
    professionalEmailVerified: contact.primaryFields?.professionalEmail?.verified,
    title: contact.primaryFields?.title?.data ?? undefined,
    stageId: contact.stageId ?? undefined,
    honorificTitle: contact.primaryFields?.honorificTitle?.data ?? null,
    displayHonorific: contact.displayHonorific ?? false,
    liOpenLink: contact.liOpenLink ?? null,
    triggeredSignals: contact.triggeredSignals ?? undefined,
    signalsComputed: contact.signalsComputed ?? false,
  };
}

export function orgOldToNewFormat(org: OrgConverterInput | null): ContactCardOrgRowOrg | null {
  if (org == null) return null;

  return {
    id: org.id,
    name: org.primaryFields?.name?.data ?? null,
    pictureUrl: org.primaryPictureUrl ?? null,
  };
}

export function cadenceStepsOldToNewFormat(
  steps: CadenceStepConverterInput[] | null | undefined,
): ContactCardBodyCadenceStep[] | null | undefined {
  return steps?.map((step) => ({
    id: step.id,
    parent_id: step.parent_id,
    type: step.type,
    state: step.state,
    option: step.option,
    archived: step.archived,
    computedError: step.computedError,
  }));
}

/**
 * Build the router-query update that opens a contact's detail panel.
 * Use at <ContactCardPure onClick={...}> sites:
 *
 *   onClick={() => setQuery((q) => openContactDetailQuery(q, { contactId, cadenceId }))}
 *
 * Pass `dontClearOrg: true` when the contact is being opened from an org page
 * and you want to keep the org in the URL.
 */
export function openContactDetailQuery(
  prev: SearchParams,
  args: { contactId: string; cadenceId?: string; dontClearOrg?: boolean },
): SearchParams {
  return {
    ...prev,
    id: joinIds({ contactId: args.contactId, cadenceId: args.cadenceId }),
    ...(args.dontClearOrg ? {} : { orgId: undefined }),
    stepId: undefined,
    companyName: undefined,
    scheduledStepId: undefined,
    hubspotId: undefined,
    duplicateId: undefined,
    detailType: prev.detailType ?? 'interact',
  };
}

/**
 * Build the router-query update that closes the contact/org detail panel.
 * Inverse of `openContactDetailQuery`: clears every panel-scoped param —
 * including `detailType`, which is purely panel state — so the URL returns to
 * the underlying list. List filters such as `response` are preserved so the
 * list keeps the filter the panel was opened from.
 *
 *   onClose={() => setQuery((q) => closeContactDetailQuery(q))}
 */
export function closeContactDetailQuery(prev: SearchParams): SearchParams {
  return {
    ...prev,
    id: undefined,
    orgId: undefined,
    detailType: undefined,
    stepId: undefined,
    companyName: undefined,
    scheduledStepId: undefined,
    hubspotId: undefined,
    duplicateId: undefined,
  };
}
